import { floatToDecimal } from '$lib/RLogUtils';
import type { Nullable } from '$lib/types/RoastLog';

const arrayToInt = (nums: number[]): number => {
  let val = 0;
  for (let i = 0; i < nums.length; i++) {
    const n = nums[i];
    val += n << (8 * (nums.length - (i + 1)));
  }
  return val;
};

const toPressure = (val: number): Nullable<number> => {
  if (!val) {
    return null;
  }
  val /= Math.pow(2, 4);
  const result = floatToDecimal(1000 * (val * (65 / 1023.0) + 50));
  return result > 101300 ? 101300 : result;
};

const toHumidity = (val: number): Nullable<number> => {
  if (!val) {
    return null;
  }
  const result = floatToDecimal((val * 100) / Math.pow(2, 14));
  return result > 100 ? 100 : result;
};

const toTempRTD = (val: number): number => {
  const vRtd = toMilliVolt(val, 1.5, 20);
  if (vRtd === null) {
    return 0;
  }
  return millivoltsToTemp(vRtd);
};

const millivoltsToTemp = (m: number): number => {
  const rRtd = (m * 10000) / (3 - m);

  const r = 1000;
  const a = 3.908 * Math.pow(10, -3);
  const b = -5.775 * Math.pow(10, -7);

  const first = -r * a;
  const root = Math.sqrt(Math.pow(r, 2) * Math.pow(a, 2) - 4 * r * b * (r - rRtd));
  const denom = 2 * r * b;

  return (first + root) / denom;
};

const toMilliVolt = (val: number, Vs = 46.875, rounding = 4): number | null => {
  // Shift 20 bits to get the header and important bits inside the header
  const header = val >> 20;
  const msb = header & 1;
  const sig = (header >> 1) & 1;

  // shift right four bit to get rid of the extra zeros and mask to get just
  // the four bytes of data
  let data = (val >> 4) & 0xffff;

  if (sig === 1 && msb === 0) {
    // normal in-range case...subtract 1
    data -= 1;
  } else if (sig === 0 && msb === 1) {
    // negative, do 2's compliment
    data = Math.abs(data - (1 << 16));
    Vs *= -1;
  } else if (sig === 1 && msb === 1) {
    // out of range high
    // TODO figure out how we want to handle this
    // error('OUT OF RANGE HIGH')
    return null;
  } else if (sig === 0 && msb === 0) {
    // out of range low
    // error('OUT OF RANGE LOW')
    return null;
  }

  // return differential millivolts
  return floatToDecimal((data * Vs) / 65536, rounding);
};

export { arrayToInt, toHumidity, toMilliVolt, toPressure, toTempRTD };
